/* The route, as a pure function of a URL fragment.
 *
 * ⚠️ THE ROSTER DID NOT EARN A ROUTER AND `App.tsx` SAID SO. Signed-out shows sign-in and
 * signed-in shows the roster: that is a CONDITION, not a route. **The client page is what earns
 * one** — the first screen you can be on while signed in that is not the roster — and it is the
 * same moment the roster's name column finally gets somewhere to link to, which is why no row
 * click was faked in session one.
 *
 * ── HASH ROUTING, AND IT IS A DEPLOYMENT DECISION RATHER THAN A TASTE ONE ──────────────────
 *
 * `docs/WEB-APP-PLAN.md` §1 chose a static SPA precisely so that **nothing here forces a money
 * decision** — a static build sits on any free host. History-API routing breaks that: every
 * static host needs a rewrite rule sending unknown paths to `index.html`, and every host spells
 * that rule differently. A deep link into a client page would 404 on a host nobody had
 * configured yet, on a host that has not been chosen yet.
 *
 * A fragment is never sent to a server, so `#/client/<id>` loads `index.html` everywhere,
 * including `file://` and `vite preview`. The cost is the visible `#`. That is the whole cost,
 * and it is smaller than a deployment prerequisite the owner has not been asked about.
 *
 * ── AND NO ROUTER LIBRARY ─────────────────────────────────────────────────────────────────
 *
 * Two routes. `react-router` would be a dependency whose API is larger than the problem, and —
 * the load-bearing half — **it would not do the thing that actually matters here anyway**: on a
 * client-side navigation the browser does not move focus or announce anything, so a keyboard or
 * screen-reader user is left standing on a link that has vanished. §12 measured the competitor's
 * primary work surface at ZERO focusable elements, and "better than Everfit" is free to claim
 * only if the navigation is genuinely operable. Focus management is `App.tsx`'s job below, and
 * hand-rolling the parse is what keeps it visible rather than assumed.
 */

export type Route =
  /**
   * THIS WEEK — the landing (W72 step 1, Proposal 1): one board across every client, the weekly loop
   * first. `#/` is its address; `#/week` is accepted as a spelling of the same place.
   */
  | { name: "dashboard" }
  | { name: "roster" }
  /**
   * AN INVITATION LINK — the ONE route addressed by PATH rather than by fragment.
   *
   * 🔴 EVERY OTHER ROUTE IS A FRAGMENT AND SHOULD STAY ONE; the reasoning at the head of this file
   * still holds for them. This one cannot be, and the reason is the official app rather than the
   * web: **Apple matches a universal link on the PATH, and a fragment is never sent to the
   * server at all.** `coachwork.io/#/join/<token>` can therefore never become a link that opens
   * the app — its path is `/`. `coachwork.io/join/<token>` can.
   *
   * ⚠️ The objection the fragment choice was made against — *"every static host needs a rewrite
   * rule and each spells it differently"* — was true when no host had been chosen. Netlify IS the
   * host now and `netlify.toml` is in this repo, so the rewrite is ours to write, once, beside
   * the config it belongs to.
   */
  | { name: "join"; token: string }
  | { name: "client"; clientId: string }
  /** The workout library — the coach's own templates. */
  | { name: "workouts" }
  /**
   * THE EXERCISE SHELF — the movements the workouts are built FROM (W1, 2026-09-18).
   *
   * ⚠️ IT HAS ITS OWN ADDRESS BUT IS THE **SAME SECTION** AS `workouts`, and both halves matter.
   * Its own address, because a shelf should be deep-linkable and because the editor is a window
   * over it — a tab a screen merely remembers cannot be either. The same section, because a
   * FOURTH rail entry is a PRODUCT decision (`SectionId`'s own comment says so, and it is his),
   * and because the phone's model is ONE Library holding two lists. The compatibility rule binds
   * the MODEL, not the breadth — `CLAUDE.md`: *"of course the web app is gonna have more
   * functions than the app, but make sure they are compatible"*.
   */
  | { name: "exercises" }
  /** The editor on an exercise that does not exist yet. */
  | { name: "exerciseNew" }
  /** The editor on an existing exercise. */
  | { name: "exercise"; exerciseId: string }
  /** The builder on a workout that does not exist yet. */
  | { name: "workoutNew" }
  /** The builder on an existing workout. */
  | { name: "workout"; workoutId: string }
  /**
   * W17d — THE WORKOUT AT A GLANCE, as a window over the library (§7.1, his word 2026-09-19).
   *
   * 🔴 IT IS A ROUTE AND NOT A PIECE OF COMPONENT STATE, FOR THE REASON THIS FILE ALREADY GIVES
   * ONE SCREEN DOWN: *"a modal opened by a click has no address"*. The overview is reached from a
   * hover-revealed icon, and if it lived in `Workouts`' state then Escape, the back button and a
   * pasted link would all mean something different here than they do for the builder — three
   * behaviours a coach would have to learn twice. Its address also makes the ONE thing §7.1 asks
   * for expressible: *"if edit is clicked, will turn this window into the real one"* is a
   * navigation from `#/workouts/<id>/overview` to `#/workouts/<id>`, which keeps the SAME
   * `<dialog>` open — same backdrop, no close, no second window — because `backdropRoute` names
   * `workouts` for both.
   */
  | { name: "workoutOverview"; workoutId: string }
  /**
   * PROGRAMS — a multi-week block a coach saves once and assigns whole (W30(e),
   * `docs/PROGRAMS-DESIGN.md`).
   *
   * ⚠️ THREE ADDRESSES, AND THE **LIBRARY** SECTION, NOT A FOURTH RAIL ENTRY. `SectionId`'s own
   * comment records that a fourth section is PRODUCT scope and HIS; W1 put the exercise shelf
   * under Library on exactly that ground and this is the same argument one object further on —
   * a program holds NOTHING but references to the coach's own templates, so it is a library
   * object made of library objects. Own addresses so a block can be deep-linked and pasted.
   */
  | { name: "programs" }
  /**
   * EQUIPMENT — the coach's own equipment settings (his direction, 2026-09-23: *"we need a place
   * to create equipment … maybe in the library? As a category"*). A FOURTH LIST in the Library, not
   * a fourth rail entry — the same argument the shelf and programs made: a library object.
   */
  | { name: "equipment" }
  /** The board on a program that does not exist yet. */
  | { name: "programNew" }
  /**
   * The board on an existing program.
   *
   * ⚠️ A SCREEN, NOT A WINDOW, and `backdropRoute` says so below. §4: *"the board is a screen,
   * not a window: it is where a coach works for minutes"* — the window mechanism exists for an
   * editor you work AGAINST a backdrop, and there is nothing behind a block that helps you plan
   * it.
   */
  | { name: "program"; programId: string }
  /**
   * THE INTAKE INBOX — who has asked to be coached (W5, 2026-09-18).
   *
   * ⚠️ IT SITS UNDER **CLIENTS**, not in a section of its own: a request is a person who wants to
   * become one, and a fourth rail entry is PRODUCT scope and his. Its own ADDRESS so it can be
   * linked, and so the rail marks Clients current while you stand on it.
   */
  | { name: "requests" }
  /**
   * THE PUBLIC IDENTITY — the name and face a coach shows the community (W6, 2026-09-18).
   *
   * ⚠️ Reached from the ACCOUNT row like the coach's own page, and marking NOTHING current in the
   * rail — it is about the person, not about a section of the work. `sectionOf` returns null for
   * it for the same reason it does for `page`.
   */
  | { name: "identity" }
  /**
   * W72 STEP 8 — THE ACCOUNT'S TWO TRUST PAGES: `Your data` (take your work with you) and `What changed` (the
   * changelog, rendered from the repo's `CHANGELOG.md`). About the coach, not a place in the work — reached from
   * the account row and the account's tab row, marking nothing current in the rail (`sectionOf` → null).
   */
  | { name: "data" }
  | { name: "changes" }
  /** W82 STEP 6 — THE COACH'S REMINDERS (`coach_reminders`), all of them grouped by day; under the Account, beside the
   *  requests — about the coach's own working day, not a place in the work (`sectionOf` → null). */
  | { name: "reminders" }
  /**
   * The message threads.
   *
   * ⚠️ THE ROUTE EXISTS AND THE SURFACE DOES NOT, WHICH IS A DELIBERATE ASYMMETRY. B-2 shipped
   * `coach_messages` and the phone screens; there is no web reader yet (`docs/WEB-APP-PLAN.md`
   * §9.2 records it as "table + phone screens exist; no web surface"). The section is in the
   * shell anyway because the shell's job is to make the PRODUCT visible, and messaging is part
   * of the product — a coach who cannot find it on the web concludes the app has none. The
   * screen says where the threads are today rather than pretending to be one.
   */
  | { name: "inbox" }
  /**
   * THE COACH'S OWN PAGE — the profile clients find them by.
   *
   * ⚠️ IT IS A ROUTE AND NOT A MODAL, because it is a DESTINATION a coach returns to: they write
   * it, publish it, and come back to change a line. A window over the roster would say the
   * opposite — that this is a detour from the real work — and it is the one screen without which
   * a web-signed-up coach is invisible and cannot post.
   */
  | { name: "page" }
  /**
   * One conversation, addressed by the client it is with.
   *
   * ⚠️ THE PAIR IS THE THREAD AND ONLY HALF OF IT IS IN THE URL, WHICH IS CORRECT RATHER THAN
   * LOSSY. `coach_messages` is keyed on (coach, client); the coach is whoever is signed in, so
   * putting them in the address would let one coach type another coach's id into a link and
   * discover nothing — RLS returns no rows — while making every link longer and wrong to share.
   * The signed-in half of a key never belongs in a URL.
   */
  | { name: "thread"; clientId: string }
  /**
   * ONE FINISHED WORKOUT, REVIEWED (W82 §2) — `#/review/<sessionId>`, where the Dashboard's `Review next ›` and each queue
   * row's `Review` land. Registered in step 2 so the link is real and routed; step 3 builds the page (a placeholder says so
   * until then). The session id alone, like a thread's client id: the coach is whoever is signed in, and RLS decides
   * whether the row is theirs to read.
   */
  | { name: "review"; sessionId: string }
  /**
   * W93 (his word, 2026-09-28): a Dashboard queue row opens the workout's OVERVIEW — what was written and what was done —
   * as a window over the Dashboard, `#/review/<sessionId>/overview`, with its own Review button; the row's Review button
   * still goes straight into the review. `overview` is matched EXACTLY, like the library's.
   */
  | { name: "sessionOverview"; sessionId: string }
  /**
   * 2026-10-07 (his idea: *"an edit workout thing or pencil for the whole thing … which will bring the workout edit
   * window"*) — the review's `Edit next week`: the WORKOUT BUILDER on next week's COPY, as a window over the review it was
   * opened from, `#/review/<sessionId>/next-week/<workoutId>`. Closing goes back to the review (`backdropRoute`).
   */
  | { name: "reviewNextWeek"; sessionId: string; workoutId: string }
  /**
   * William's item 4 (2026-10-15, `docs/WILLIAM-ITEM4-PLAN.md`) — THE BUILDER OVER A CLIENT'S WEEK. `Build a workout` in the
   * day's assign window opens the SAME builder EMPTY for that client and day, `#/clients/<id>/workouts/new/<YYYY-MM-DD>`;
   * nothing is written until its first Save (`create_client_workout`). The day card's pencil opens it on THAT day's copy,
   * `#/clients/<id>/workouts/<workoutId>`. Both sit over the client page (`backdropRoute`) and keep its query
   * (`hrefKeepingQuery`), so the week behind does not move.
   */
  | { name: "clientWorkoutNew"; clientId: string; day: string }
  | { name: "clientWorkout"; clientId: string; workoutId: string }
  /**
   * THE REVIEWER SEAT (step 2, 2026-10-10, `docs/REVIEWER-SEAT-BRIEF.md`) — a client who invited this account to REVIEW
   * his week: `#/reviewing/<clientId>` (his finished days + the proposed weeks), `#/reviewing/<clientId>/day/<sessionId>`
   * (the review page in reviewer mode), `#/reviewing/<clientId>/week/<monday>` (the builder in reviewer mode — a COPY of
   * that week, saved as a proposal). Never among the coach's clients; the server decides (`reviewer_is_active`).
   */
  | { name: "reviewing"; clientId: string }
  | { name: "reviewingDay"; clientId: string; sessionId: string }
  | { name: "reviewingWeek"; clientId: string; monday: string }
  /** An address this app does not have. Named, so the screen can say so rather than blanking. */
  | { name: "unknown"; raw: string };

/**
 * Parse `location.hash`.
 *
 * ⚠️ AN UNRECOGNISED FRAGMENT IS `unknown`, NEVER SILENTLY THE ROSTER. Falling back to the home
 * screen is the friendlier-looking choice and is the same shape as every fallback this repo has
 * been bitten by: a typo'd or stale link would land somewhere plausible with nothing to notice.
 * `CLAUDE.md`'s twentieth instance is exactly this — an unmatched string and `nil` being
 * indistinguishable to the reader — so the two are kept apart here by construction.
 *
 * The empty hash IS the landing — This week since W72 step 1 (it was the roster before).
 */
/**
 * The query part of a hash route — `#/clients?sort=name` → `{sort: "name"}`.
 *
 * 🔴 IT EXISTS BECAUSE THE ROUTER WOULD OTHERWISE CALL A SORTED ROSTER `unknown`. The design
 * contract says the URL is the state wherever a coach could link to a view, and a sorted roster
 * is exactly that. But `parseRoute` matched the WHOLE fragment against a path, so the first
 * `?sort=` would have fallen through every branch to `unknown` — a blank screen for a link that
 * looks right, which is the twentieth-instance shape this file's own header warns about.
 *
 * So the query is stripped BEFORE matching and read separately. A route with no query returns an
 * empty map rather than null: the caller asks "what is the sort" and gets "nothing was asked
 * for", never "there is no query object".
 */
export function routeQuery(hash: string): Record<string, string> {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const q = raw.indexOf("?");
  if (q === -1) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(raw.slice(q + 1))) out[k] = v;
  return out;
}

/**
 * William's item 4 — an address carrying the CURRENT page's query (its tab, week, span…) with the assign picker's `view`
 * dropped: the builder over a client's week opens and closes without moving the page behind it (`date` is kept, so the
 * calendar stays on that week — `withoutPicker`'s rule).
 */
export function hrefKeepingQuery(route: Route, hash: string): string {
  const q = new URLSearchParams(routeQuery(hash));
  q.delete("view");
  const s = q.toString();
  return `${href(route)}${s === "" ? "" : `?${s}`}`;
}

/**
 * WHERE THIS APP LIVES, for the one link that is shared outside it.
 *
 * ⚠️ NOT `window.location.origin`: an invitation composed while a coach happens to be on
 * `localhost:5173` would carry a link nobody else can open, and it would look perfectly correct
 * on the screen that made it.
 */
export const SITE_ORIGIN = "https://coachwork.io";

export function parseRoute(hash: string, pathname = ""): Route {
  // ⚠️ THE PATH IS CONSULTED FIRST AND ONLY FOR `/join`. Reading the pathname for anything else
  // would quietly make every route path-addressable and break the deployment property the rest of
  // this file rests on.
  const joined = /^\/join\/([A-Za-z0-9_-]{8,})\/?$/.exec(pathname);
  if (joined) return { name: "join", token: joined[1] ?? "" };

  const raw0 = hash.startsWith("#") ? hash.slice(1) : hash;
  // ⚠️ THE QUERY IS NOT PART OF THE PATH. See `routeQuery` — without this line `#/clients?sort=name`
  // is an unrecognised fragment and resolves to `unknown`.
  const q = raw0.indexOf("?");
  const raw = q === -1 ? raw0 : raw0.slice(0, q);
  const path = raw.startsWith("/") ? raw.slice(1) : raw;
  if (path === "") return { name: "dashboard" };

  // The fragment form is accepted TOO, so a link that has been through something that eats paths
  // still lands. One route, two spellings, and the path form is the one we publish.
  const fragJoin = path.match(/^join\/([A-Za-z0-9_-]{8,})$/);
  if (fragJoin) return { name: "join", token: fragJoin[1] ?? "" };

  const parts = path.split("/").filter((p) => p !== "");
  // W81 (the pro web step 1): the landing is DASHBOARD. `#/dashboard` is its name; `#/week` (W72's) and `#/this-week`
  // stay as ALIASES, so a bookmark or a pasted link from before the rename still lands (route.test.ts drives each one).
  if (parts.length === 1 && (parts[0] === "dashboard" || parts[0] === "week" || parts[0] === "this-week")) return { name: "dashboard" };
  if (parts.length === 1 && parts[0] === "clients") return { name: "roster" };
  if (parts.length === 1 && parts[0] === "workouts") return { name: "workouts" };
  if (parts.length === 1 && parts[0] === "exercises") return { name: "exercises" };
  if (parts.length === 1 && parts[0] === "programs") return { name: "programs" };
  if (parts.length === 1 && parts[0] === "equipment") return { name: "equipment" };
  if (parts.length === 1 && parts[0] === "requests") return { name: "requests" };
  if (parts.length === 1 && parts[0] === "identity") return { name: "identity" };
  if (parts.length === 1 && parts[0] === "data") return { name: "data" };
  if (parts.length === 1 && parts[0] === "changes") return { name: "changes" };
  if (parts.length === 1 && parts[0] === "reminders") return { name: "reminders" };
  if (parts.length === 1 && parts[0] === "inbox") return { name: "inbox" };
  if (parts.length === 1 && parts[0] === "page") return { name: "page" };

  // ⚠️ `new` IS A RESERVED SEGMENT, AND SAYING SO IS CHEAPER THAN DISCOVERING IT. `#/workouts/new`
  // and `#/workouts/<id>` share a shape, so the literal has to be matched BEFORE the id branch or
  // a workout could never be created. It is safe because ids here are uuids and `new` is not one
  // — but the safety is worth stating rather than resting on, since the day something else mints
  // an id the two would collide silently and the builder would try to load a workout called
  // "new". A reserved word compared before a wildcard is the same rule as an exact policy match
  // before a fallback.
  if (parts.length === 2 && parts[0] === "workouts" && parts[1] === "new") {
    return { name: "workoutNew" };
  }
  // `new` before the wildcard, for the reason stated above the workout branch: a reserved segment
  // compared before an id, or an exercise could never be created.
  if (parts.length === 2 && parts[0] === "exercises" && parts[1] === "new") {
    return { name: "exerciseNew" };
  }
  // `new` before the wildcard, for the reason stated above the workout branch.
  if (parts.length === 2 && parts[0] === "programs" && parts[1] === "new") {
    return { name: "programNew" };
  }
  if (parts.length === 2 && parts[0] === "programs") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "program", programId: id };
  }

  if (parts.length === 2 && parts[0] === "exercises") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "exercise", exerciseId: id };
  }

  if (parts.length === 2 && parts[0] === "workouts") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "workout", workoutId: id };
  }

  /* W17d's overview. ⚠️ `overview` IS MATCHED EXACTLY, and anything else in that slot is
     `unknown` rather than a fall-back to the builder — the same rule the `new` branch above
     states: an unrecognised fragment must never land somewhere plausible, because that is the
     twentieth-instance shape (an unmatched string and nil being indistinguishable to the reader)
     this file's own header warns about. */
  if (parts.length === 3 && parts[0] === "workouts" && parts[2] === "overview") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "workoutOverview", workoutId: id };
  }

  if (parts.length === 3 && parts[0] === "review" && parts[2] === "overview") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "sessionOverview", sessionId: id };
  }

  if (parts.length === 4 && parts[0] === "review" && parts[2] === "next-week") {
    const [rawSession, rawWorkout] = [parts[1], parts[3]];
    if (rawSession === undefined || rawWorkout === undefined) return { name: "unknown", raw };
    let sessionId: string, workoutId: string;
    try { sessionId = decodeURIComponent(rawSession); workoutId = decodeURIComponent(rawWorkout); } catch { return { name: "unknown", raw }; }
    return sessionId === "" || workoutId === "" ? { name: "unknown", raw } : { name: "reviewNextWeek", sessionId, workoutId };
  }

  if (parts[0] === "reviewing" && (parts.length === 2 || parts.length === 4)) {
    let ids: string[];
    try { ids = parts.slice(1).map((x) => decodeURIComponent(x)); } catch { return { name: "unknown", raw }; }
    const [clientId = "", kind, third = ""] = ids;
    if (clientId === "") return { name: "unknown", raw };
    if (parts.length === 2) return { name: "reviewing", clientId };
    if (kind === "day" && third !== "") return { name: "reviewingDay", clientId, sessionId: third };
    if (kind === "week" && /^\d{4}-\d{2}-\d{2}$/.test(third)) return { name: "reviewingWeek", clientId, monday: third };
    return { name: "unknown", raw };
  }

  if (parts.length === 2 && parts[0] === "review") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "review", sessionId: id };
  }

  if (parts.length === 2 && parts[0] === "inbox") {
    const rawId = parts[1];
    if (rawId === undefined) return { name: "unknown", raw };
    let id: string;
    try { id = decodeURIComponent(rawId); } catch { return { name: "unknown", raw }; }
    return id === "" ? { name: "unknown", raw } : { name: "thread", clientId: id };
  }

  /* William's item 4 — `new` before the wildcard (the workouts branch's reason), and the day must be a real `YYYY-MM-DD`:
     anything else in either slot is `unknown`, never a builder on a plausible guess. */
  if (parts[0] === "clients" && parts[2] === "workouts" && (parts.length === 4 || parts.length === 5)) {
    let ids: string[];
    try { ids = parts.map((x) => decodeURIComponent(x)); } catch { return { name: "unknown", raw }; }
    const [, clientId = "", , third = "", day = ""] = ids;
    if (clientId === "" || third === "") return { name: "unknown", raw };
    if (parts.length === 5) {
      return third === "new" && /^\d{4}-\d{2}-\d{2}$/.test(day) ? { name: "clientWorkoutNew", clientId, day } : { name: "unknown", raw };
    }
    return third === "new" ? { name: "unknown", raw } : { name: "clientWorkout", clientId, workoutId: third };
  }

  const second = parts[1];
  if (parts.length === 2 && parts[0] === "clients" && second !== undefined) {
    // Decoded, because an id travels through the URL. Malformed percent-encoding throws rather
    // than returning a wrong string, so it is caught and reported as unknown.
    let id: string;
    try {
      id = decodeURIComponent(second);
    } catch {
      return { name: "unknown", raw };
    }
    return id === "" ? { name: "unknown", raw } : { name: "client", clientId: id };
  }
  return { name: "unknown", raw };
}

/**
 * The route whose screen renders BEHIND this one, dimmed — or `null` when this route is a whole
 * page in its own right.
 *
 * 🔴 THIS IS WHERE "WHICH ROUTES ARE WINDOWS" IS DECIDED, AND IT IS DELIBERATELY PART OF THE URL
 * GRAMMAR RATHER THAN A FLAG IN A COMPONENT. The owner's words were about the LOOK — *"they're
 * just windows which make the rest of the background screen darker? very clean"* — but the thing
 * that makes a window honest is its ADDRESS. A modal opened by a click has no URL, so a workout
 * link stops opening the right thing the moment the editor becomes an overlay. `#/workouts/<id>`
 * still means exactly what it meant; what changes is that the screen it names is drawn as a
 * window over the list it came from.
 *
 * Two callers, one definition. `App.tsx` reads it to know WHICH screen to draw underneath and
 * whether to draw a window at all; the window's own Close reads it to know where closing GOES.
 * Written twice, they would drift the first time a third window existed — the same reason
 * `href()` is the only place an address is built.
 *
 * ⚠️ CLOSING IS A NAVIGATION, NEVER `history.back()`. A coach who arrived by pasted link has no
 * history to go back to, so `back()` would leave the app entirely — and the two cases are
 * indistinguishable from inside the page. Setting the hash to the backdrop's address is correct
 * for both, at the cost of one extra history entry.
 */
export function backdropRoute(route: Route): Route | null {
  switch (route.name) {
    // An invitation is a whole page, not a window over one: there is nothing behind it to dim,
    // and the reader has no roster to return to.
    case "join":
      return null;
    // The builder. Both of its addresses sit over the library it belongs to — a new workout is
    // being added to that list, and an existing one was opened from it.
    case "workoutNew":
    case "workout":
    // W17d's overview sits over the SAME list, and that identity is load-bearing rather than
    // tidy: `App.tsx` draws the backdrop for `backdropRoute(route)`, so Edit navigating from the
    // overview to the builder leaves the backdrop screen MOUNTED and the `<dialog>` open. The
    // window changes what it holds; it never closes and re-opens, which is what "turn THIS
    // window into the real one" means when written as an address.
    case "workoutOverview":
      return { name: "workouts" };
    // W93: the finished workout's overview sits over the queue it was opened from.
    case "sessionOverview":
      return { name: "dashboard" };
    // 2026-10-07: next week's builder sits over the review that opened it — the review stays MOUNTED behind it (the same
    // element, keyed by the session), so its drafts survive a discard and its read runs again on a save.
    case "reviewNextWeek":
      return { name: "review", sessionId: route.sessionId };
    // William's item 4: the builder over a client's week sits over that client's page, which stays MOUNTED behind it.
    case "clientWorkoutNew":
    case "clientWorkout":
      return { name: "client", clientId: route.clientId };
    // The exercise editor, over the shelf it belongs to — the same relationship, and it earns a
    // window for the same reason: it holds unsaved work, and a coach edits an exercise AGAINST
    // the list they are comparing it with.
    case "exerciseNew":
    case "exercise":
      return { name: "exercises" };
    // ⚠️ A THREAD IS A PAGE, NOT A WINDOW, AND THAT IS DECIDED HERE RATHER THAN BY HABIT.
    // Session 10's window mechanism exists for an EDITOR — something holding unsaved work, whose
    // Escape must not discard it and whose backdrop must stay visible so you can see what you
    // are editing against. A conversation holds no unsaved state worth guarding, and it is a
    // column-2-plus-column-3 reading surface, which is the `Workspace` shape the roster and the
    // client page already use. Making it a window would dim the list you pick threads from.
    case "thread":
    // A review is a reading-and-writing PAGE the coach stands on for minutes (W82 §2), reached from the queue.
    case "review":
    case "reviewing":
    case "reviewingDay":
    // The reviewer's week is a PAGE: there is nothing behind it to edit against, and he stands on it for minutes.
    case "reviewingWeek":
    case "dashboard":
    case "roster":
    case "client":
    case "workouts":
    // ⚠️ THE BOARD IS A PAGE BY THIS FUNCTION'S OWN TEST, and it is worth saying rather than
    // resting on: the window mechanism is for an editor whose BACKDROP you edit against. A
    // program is planned against nothing — the templates it references are chosen from a picker
    // inside it — and a coach stands here for minutes, which a dimmed overlay denies.
    case "programs":
    case "programNew":
    case "program":
    case "equipment":
    case "exercises":
    case "requests":
    case "identity":
    case "data":
    case "changes":
    case "reminders":
    case "inbox":
    // ⚠️ A PAGE, NOT A WINDOW — and by this function's own test: the window mechanism exists for
    // an EDITOR whose backdrop you edit AGAINST, and there is nothing behind a coach's own page
    // that helps them write it. It is also a destination they come back to, which a window is not.
    case "page":
    case "unknown":
      return null;
  }
}

/**
 * The address of a route — the ONE place a link's `href` is built.
 *
 * A hand-written `#/clients/${id}` somewhere in a component is a second statement of the URL
 * grammar that `parseRoute` owns, and the two would drift the first time either moved. Same rule
 * as the tokens: generate the one from the other, never type both.
 */
export function href(route: Route): string {
  switch (route.name) {
    // ⚠️ THE ONE ABSOLUTE ADDRESS THIS FUNCTION PRODUCES, and it has to be: an invitation link is
    // pasted into WhatsApp, not clicked inside the app, so a fragment-relative href would be
    // useless the moment it left the page. It is also the form a universal link can match.
    case "join":
      return `${SITE_ORIGIN}/join/${encodeURIComponent(route.token)}`;
    case "dashboard":
      return "#/";
    case "roster":
      return "#/clients";
    case "client":
      return `#/clients/${encodeURIComponent(route.clientId)}`;
    case "workouts":
      return "#/workouts";
    case "exercises":
      return "#/exercises";
    case "requests":
      return "#/requests";
    case "identity":
      return "#/identity";
    case "data":
      return "#/data";
    case "changes":
      return "#/changes";
    case "reminders":
      return "#/reminders";
    case "exerciseNew":
      return "#/exercises/new";
    case "exercise":
      return `#/exercises/${encodeURIComponent(route.exerciseId)}`;
    case "workoutNew":
      return "#/workouts/new";
    case "workout":
      return `#/workouts/${encodeURIComponent(route.workoutId)}`;
    case "workoutOverview":
      return `#/workouts/${encodeURIComponent(route.workoutId)}/overview`;
    case "programs":
      return "#/programs";
    case "equipment":
      return "#/equipment";
    case "programNew":
      return "#/programs/new";
    case "program":
      return `#/programs/${encodeURIComponent(route.programId)}`;
    case "inbox":
      return "#/inbox";
    case "page":
      return "#/page";
    case "thread":
      return `#/inbox/${encodeURIComponent(route.clientId)}`;
    case "review":
      return `#/review/${encodeURIComponent(route.sessionId)}`;
    case "sessionOverview":
      return `#/review/${encodeURIComponent(route.sessionId)}/overview`;
    case "reviewNextWeek":
      return `#/review/${encodeURIComponent(route.sessionId)}/next-week/${encodeURIComponent(route.workoutId)}`;
    case "clientWorkoutNew":
      return `#/clients/${encodeURIComponent(route.clientId)}/workouts/new/${route.day}`;
    case "clientWorkout":
      return `#/clients/${encodeURIComponent(route.clientId)}/workouts/${encodeURIComponent(route.workoutId)}`;
    case "reviewing":
      return `#/reviewing/${encodeURIComponent(route.clientId)}`;
    case "reviewingDay":
      return `#/reviewing/${encodeURIComponent(route.clientId)}/day/${encodeURIComponent(route.sessionId)}`;
    case "reviewingWeek":
      return `#/reviewing/${encodeURIComponent(route.clientId)}/week/${route.monday}`;
    case "unknown":
      return "#/clients";
  }
}
